<template>
  <nav class="navbar" :class="{ scrolled: isScrolled }">
    <a href="#" class="logo" @click.prevent="scrollTo('inicio')">
      <div class="logo-icon"><i class="fas fa-home"></i></div>
      <span class="logo-text">VISION HOUSE</span>
    </a>

    <ul class="nav-links hide-mobile">
      <li><a href="#inicio" @click.prevent="scrollTo('inicio')">Inicio</a></li>
      <li><a href="#nosotros" @click.prevent="scrollTo('nosotros')">Nosotros</a></li>
      <li><a href="#pilares" @click.prevent="scrollTo('pilares')">Pilares</a></li>
      <li><a href="#programas" @click.prevent="scrollTo('programas')">Programas</a></li>
      <li><a href="#impacto" @click.prevent="scrollTo('impacto')">Impacto</a></li>
      <li><a href="#donar" class="nav-cta" @click.prevent="scrollTo('donar')">Donar</a></li>
      <li><a href="#voluntariado" @click.prevent="scrollTo('voluntariado')">Únete</a></li>
    </ul>

    <div class="mobile-menu hide-desktop" :class="{ active: menuOpen }" @click="toggleMenu">
      <span></span><span></span><span></span>
    </div>
  </nav>

  <!-- Mobile Nav Overlay -->
  <div class="mobile-nav hide-desktop" :class="{ active: menuOpen }">
    <a v-for="(item, index) in menuItems" :key="item.id" 
       :href="'#' + item.id" 
       :style="{ transitionDelay: (0.1 + index * 0.05) + 's' }"
       @click.prevent="scrollTo(item.id); toggleMenu()">
      {{ item.label }}
    </a>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';

const isScrolled = ref(false);
const menuOpen = ref(false);

const menuItems = [
  { id: 'inicio', label: 'Inicio' },
  { id: 'nosotros', label: 'Nosotros' },
  { id: 'pilares', label: 'Pilares' },
  { id: 'programas', label: 'Programas' },
  { id: 'impacto', label: 'Impacto' },
  { id: 'donar', label: 'Donar' },
  { id: 'voluntariado', label: 'Únete' },
];

let observer: IntersectionObserver | null = null;

function toggleMenu() {
  menuOpen.value = !menuOpen.value;
}

function scrollTo(id: string) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

onMounted(() => {
  // Use IntersectionObserver for highly reliable scroll detection in Ionic
  const anchor = document.getElementById('scroll-anchor');
  if (anchor) {
    observer = new IntersectionObserver(([entry]) => {
      // If anchor is NOT intersecting, we've scrolled down
      isScrolled.value = !entry.isIntersecting;
    }, { threshold: 0.1 });
    observer.observe(anchor);
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') menuOpen.value = false;
  });
});

onUnmounted(() => {
  if (observer) observer.disconnect();
});
</script>

<style scoped>
.navbar {
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1000;
  padding: 1.5rem 5%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  background: transparent;
}

/* Scrolled State */
.navbar.scrolled {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(15px) saturate(180%);
  -webkit-backdrop-filter: blur(15px) saturate(180%);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  padding: 0.8rem 5% !important;
}

/* Text Colors */
.navbar .logo-text,
.navbar .nav-links a {
  color: white;
  transition: color 0.3s ease;
}

.navbar.scrolled .logo-text,
.navbar.scrolled .nav-links a {
  color: var(--vh-azul-profundo) !important;
}

.navbar.scrolled .mobile-menu span {
  background: var(--vh-azul-profundo) !important;
}

.logo { display: flex; align-items: center; gap: 0.8rem; text-decoration: none; }
.logo-icon {
  width: 48px; height: 48px;
  background: linear-gradient(135deg, var(--vh-azul-profundo), var(--vh-azul-cielo));
  border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  color: white; font-size: 1.4rem;
  transition: var(--vh-transicion);
  box-shadow: 0 4px 15px rgba(26, 75, 140, 0.3);
}
.logo:hover .logo-icon { transform: rotate(-5deg) scale(1.05); }

.logo-text {
  font-family: 'League Spartan', sans-serif;
  font-weight: 800; font-size: 1.6rem;
  letter-spacing: -0.5px;
}

.nav-links { display: flex; gap: 2.5rem; list-style: none; align-items: center; }
.nav-links a {
  text-decoration: none;
  font-weight: 600; font-size: 0.85rem;
  position: relative;
  font-family: 'Barlow', sans-serif;
  text-transform: uppercase; letter-spacing: 1.5px;
}

.nav-links a::after {
  content: ''; position: absolute;
  bottom: -6px; left: 0;
  width: 0; height: 2.5px;
  background: var(--vh-dorado);
  transition: width 0.3s ease;
  border-radius: 2px;
}
.nav-links a:hover::after { width: 100%; }
.nav-links a:hover { color: var(--vh-dorado) !important; }

.nav-cta {
  background: var(--vh-dorado); 
  color: white !important;
  padding: 0.6rem 1.5rem; border-radius: 50px;
  font-family: 'League Spartan', sans-serif !important;
  font-weight: 700 !important;
  box-shadow: 0 4px 15px rgba(251, 166, 54, 0.3);
  transition: all 0.3s ease !important;
}
.nav-cta::after { display: none !important; }
.nav-cta:hover { 
  background: var(--vh-dorado-hover); 
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(251, 166, 54, 0.4);
}

.mobile-menu {
  display: none; flex-direction: column;
  gap: 5px; cursor: pointer;
  z-index: 1001; padding: 5px;
}
.mobile-menu span {
  width: 26px; height: 3px;
  background: white;
  transition: var(--vh-transicion);
  border-radius: 3px; display: block;
}
.mobile-menu.active span:nth-child(1) { transform: rotate(45deg) translate(5px, 5px); }
.mobile-menu.active span:nth-child(2) { opacity: 0; }
.mobile-menu.active span:nth-child(3) { transform: rotate(-45deg) translate(7px, -6px); }

.mobile-nav {
  position: fixed; top: 0; left: 0;
  width: 100%; height: 100vh;
  background: linear-gradient(135deg, var(--vh-azul-profundo), var(--vh-azul-oscuro));
  z-index: 998;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 2.5rem;
  transform: translateX(100%);
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}
.mobile-nav.active { transform: translateX(0); }
.mobile-nav a {
  color: white; text-decoration: none;
  font-family: 'League Spartan', sans-serif;
  font-size: 1.8rem; font-weight: 700;
  opacity: 0; transform: translateY(20px);
  transition: all 0.4s ease;
}
.mobile-nav.active a { opacity: 1; transform: translateY(0); }

@media (max-width: 768px) {
  .mobile-menu { display: flex; }
}
</style>
